import { buildTutorial } from './layouts/tutorial.js';
import { z } from 'zod';
import { isNorthBank } from '../world/banks.js';
import { CollisionGrid } from '../world/collision.js';
import { TerrainField } from '../world/terrain-field.js';
import { DUNGEON_HEIGHT, DUNGEON_WIDTH } from './layouts/boss-dungeons.js';
import { AERIE_HEIGHT, AERIE_WIDTH, buildShefiraAerie } from './layouts/shefira-aerie.js';
import { SHEFIRA_ZONE_ID } from './shefira.js';
import { applyWorldEdits } from './layouts/apply-world-edits.js';
import { WORLD_SIZE } from './layouts/world.js';
import { AERIE_GATE_PROP } from './layouts/world-aerie.js';
import { restoreWorld } from './layouts/world-snapshot.js';
import { ChestPlacementSchema } from './chests.js';
import { COMPANY_FALLEN_MOBS, COMPANY_LAST_STAND, COMPANY_MARCHERS } from './company.js';
import { MobIdSchema } from './mobs.js';
import { footprintOf, inDoorway, PropPlacementSchema, PROPS, silhouetteOf } from './props.js';
import { isTerrainChar, terrainOf } from './terrains.js';
import { WORLD_EDITS } from './world-edits.js';
import { placeQuestKeepers } from './layouts/quest-keepers.js';
import { QUEST_KEEPERS } from './quests/regions.js';
import { GENERATED_WORLD_SNAPSHOT } from './world.generated.js';
import { BIOME_IDS, BIOMES } from './biomes.js';
import { WANTED_BOARD_MOB_ID } from './bounties.js';
const TileSchema = z.object({ x: z.number().int().min(0), y: z.number().int().min(0) });
export { WORLD_SIZE };
/**
 * Zone definitions: the static shape of a piece of the world.
 *
 * The overworld is ONE zone (ADR 0010): a continuous valley with its biomes,
 * generated by `layouts/world.ts` so the server and client agree on the same
 * ground, collision and props. Further zones are for instances (dungeons),
 * which is what the exits and the per-zone rooms remain for.
 */
export const ZoneDefinitionSchema = z.object({
    id: z.string().min(1).max(32),
    name: z.string().min(1),
    width: z.number().int().min(1).max(1024),
    height: z.number().int().min(1).max(1024),
    /** Where a new or returning player lands when their saved tile is unusable. */
    spawn: z.object({ x: z.number().int().min(0), y: z.number().int().min(0) }),
    /**
     * Where a fallen player gets back up. Distinct from `spawn` so a zone can
     * put its shrine away from where newcomers arrive; defaults to `spawn`.
     */
    respawn: z.object({ x: z.number().int().min(0), y: z.number().int().min(0) }).optional(),
    /** One string per row, `#` blocked. Length must match width and height. */
    rows: z.array(z.string()).min(1),
    /**
     * What the terrain paints, one string per row, one terrain character per
     * tile (`data/terrains.ts`). Independent of collision, so a trail can be
     * walked and a tree can stand on grass. Without it, blocked tiles are
     * painted as dirt, walkable ones as grass.
     */
    ground: z.array(z.string()).optional(),
    /**
     * Height of each tile, one digit per tile (ADR 0020). A tile between two
     * heights is a cliff, blocked in `rows` unless stairs climb it. Display
     * and generation only: walking goes by `rows`. Flat without it.
     */
    heights: z.array(z.string().regex(/^[0-9]*$/)).optional(),
    /** Decorations standing on tiles; the blocking ones add to collision. */
    props: z.array(PropPlacementSchema).default([]),
    /**
     * Biome of each tile, one digit per tile (index into BIOME_IDS), for the
     * minimap and for telling a player where they are. Display only.
     */
    biomes: z.array(z.string()).optional(),
    /** Named places — villages, places of interest — for the minimap. Display only. */
    landmarks: z
        .array(z.object({
        name: z.string().min(1),
        x: z.number().int(),
        y: z.number().int(),
        /** Tiles around it that count as being there. */
        radius: z.number().int().min(1).default(12),
        /** A village shelters and trades; a place of interest is to explore; a lair holds a boss (ADR 0024). */
        kind: z.enum(['village', 'place', 'lair']).default('place'),
        /**
         * Its name until the player's story burns it (a dragon's mark, ADR
         * 0067): the quest names its props' `before` too. Display only.
         */
        before: z.object({ quest: z.string().min(1), name: z.string().min(1) }).optional(),
    }))
        .default([]),
    /**
     * The villages' portals (ADR 0017): the prop's tile, the tile one lands on
     * and stands on to use it, and the biome that prices a journey there.
     */
    portals: z
        .array(z.object({
        id: z.string().regex(/^[a-z][a-z-]{0,31}$/),
        /** The village's name, shown in the travel window. */
        name: z.string().min(1),
        biome: z.enum(BIOME_IDS),
        x: z.number().int().min(0),
        y: z.number().int().min(0),
        arrival: TileSchema,
    }))
        .default([]),
    /**
     * Ways out. Stepping on any tile of the rectangle sends the player to
     * `to`, standing on `arrive` — which must be walkable there and must not
     * itself be an exit, or the two zones would bounce the player forever.
     */
    exits: z
        .array(z.object({
        x: z.number().int().min(0),
        y: z.number().int().min(0),
        width: z.number().int().min(1).default(1),
        height: z.number().int().min(1).default(1),
        to: z.string().min(1),
        arrive: z.object({ x: z.number().int().min(0), y: z.number().int().min(0) }),
        /**
         * Taken only when the walk was meant to end there (a ladder, a cave's mouth): a player
         * crossing the hatch on their way elsewhere stays where they are.
         */
        clickOnly: z.boolean().default(false),
    }))
        .default([]),
    /**
     * The ways into an instanced zone (ADR 0068): tiles of this zone where its
     * entrance stands (the crevasse of La Brèche). No exit: stepping there
     * takes nobody anywhere; a player asks to go in (`aerie_enter`) while
     * standing by it, and the server lets them in, alone or with their raid.
     */
    gates: z
        .array(z.object({
        zone: z.string().min(1).max(32),
        x: z.number().int().min(0),
        y: z.number().int().min(0),
        width: z.number().int().min(1).default(1),
        height: z.number().int().min(1).default(1),
    }))
        .default([]),
    /** Where monsters live. Each point keeps `count` mobs alive within `radius` tiles. */
    spawns: z
        .array(z.object({
        mob: MobIdSchema,
        x: z.number().int().min(0),
        y: z.number().int().min(0),
        count: z.number().int().min(1).max(20).default(1),
        radius: z.number().int().min(0).max(8).default(0),
        /** Which way its mobs stand when they appear; south, facing the camera, by default. */
        facing: z.enum(['north', 'east', 'south', 'west']).optional(),
        /**
         * The guardians of a place of interest: the same creatures, but they may
         * leave their biome's rare upgrade material (ADR 0013, `data/loot.ts`).
         */
        guardian: z.boolean().default(false),
        /**
         * The camp its mobs keep (ADR 0021): the camp's chest unlocks when
         * every one of them is dead, and they come back together.
         */
        camp: z.string().min(1).max(32).optional(),
        /**
         * Attacks on sight even if its kind is passive (ADR 0058): a cave's
         * creatures (ADR 0062) are not walked past.
         */
        hostile: z.boolean().default(false),
        /**
         * Each player's own (ADR 0065): nobody shares them. Every player who
         * comes into the zone gets the spawn's creatures, seen and fought by
         * them and their party alone, back for them after the usual delay.
         * For the tutorial's rats, which nobody may keep from the others.
         */
        personal: z.boolean().default(false),
        /**
         * A personal spawn (ADR 0067) seen by its owner alone, not by their
         * party: each player has their own journey. The Company's recruits.
         */
        solo: z.boolean().default(false),
        /**
         * A personal spawn that stands up only for a player who turned in
         * this quest (a recruit at the Company's quarter, ADR 0067), or made
         * its stand (the memorial and Ulfar after the last stand, ADR 0068):
         * nothing is stored, it follows from the quests done.
         */
        unlockedBy: z.string().min(1).max(32).optional(),
        /**
         * A personal spawn that stands up only while this quest is under
         * way for its owner, and goes when it is turned in or given up: the
         * creatures a quest sends the player to fight at a dragon's mark,
         * which no den near it holds (ADR 0087).
         */
        during: z.string().min(1).max(32).optional(),
        /**
         * Gone for a player whose story went past this quest (ADR 0068 §4):
         * its creatures stay in the room for everyone, but a player who made
         * the quest's stand, or turned it in, no longer sees them (their
         * StateView leaves them out, like the area of interest). The Company
         * who fell in Shefira's Aire. Nothing is stored: it follows from the
         * quest's flags.
         */
        goneAfter: z.string().min(1).max(32).optional(),
        /**
         * Seen only by a player who took this quest, under way or done: the
         * Company waiting at La Brèche once the last stand is accepted.
         */
        appearsOn: z.string().min(1).max(32).optional(),
        /**
         * Gone for a player once they take this quest: the Company leaves its
         * quarter for La Brèche when the last stand is accepted (`appearsOn`
         * of their copies there). Nothing is stored either.
         */
        leavesOn: z.string().min(1).max(32).optional(),
        /**
         * An elite's lairs (ADR 0023): it comes back in one of them, drawn at
         * random each time, so nobody can wait for it at a known spot. Empty
         * for every other spawn, which always comes back home.
         */
        lairs: z
            .array(z.object({ x: z.number().int().min(0), y: z.number().int().min(0) }))
            .max(8)
            .default([]),
    }))
        .default([]),
    /** Chests (ADR 0021): camp chests and hidden ones. Each blocks its footprint. */
    chests: z.array(ChestPlacementSchema).default([]),
    /**
     * A room that stays open with nobody in it, created at boot (a boss's
     * dungeon, ADR 0027): what lives there keeps its own clock, and nobody
     * brings a slain boss back by leaving and coming in again.
     */
    persistent: z.boolean().default(false),
    /**
     * One room per fight (ADR 0068 §1): its rooms are split by `instanceId` too,
     * each opened when the server lets a player or a raid in and closed when
     * it empties. Only the characters it admitted may join one. Never
     * `persistent`, never opened at boot.
     */
    instanced: z.boolean().default(false),
});
/**
 * The world as the generator makes it, before the admin's retouches: what
 * the in-game editor names each element by (ADR 0022). Read from the output
 * of `pnpm build:world` rather than generated here (ADR 0042): generating it
 * took seconds on a laptop and half a minute on a phone, in every browser.
 */
export const GENERATED_WORLD = restoreWorld(GENERATED_WORLD_SNAPSHOT);
const WORLD = applyWorldEdits(GENERATED_WORLD, WORLD_EDITS).layout;
/**
 * The Aire's way in where the chasm stands once the admin's retouches are
 * applied (ADR 0022): the editor may move the prop, and the gate and the
 * way back out must follow it, or a click on the chasm opens nothing. The
 * generator's gate moved by the prop's own shift; null if the chasm is gone.
 */
function aerieGateOf() {
    const generated = GENERATED_WORLD.aerieGate;
    const before = GENERATED_WORLD.props.find((prop) => prop.prop === AERIE_GATE_PROP);
    const after = WORLD.props.find((prop) => prop.prop === AERIE_GATE_PROP);
    if (generated === null || before === undefined || after === undefined)
        return null;
    const dx = after.x - before.x;
    const dy = after.y - before.y;
    const box = footprintOf(after);
    return {
        area: { x: box.x0, y: box.y0, width: box.x1 - box.x0 + 1, height: box.y1 - box.y0 + 1 },
        arrival: { x: generated.arrival.x + dx, y: generated.arrival.y + dy },
        landmark: {
            ...generated.landmark,
            centre: { x: generated.landmark.centre.x + dx, y: generated.landmark.centre.y + dy },
        },
    };
}
const AERIE_GATE = aerieGateOf();
/**
 * A wanted board on every village's square (ADR 0096), stood like a keeper
 * beside the portal, so the boards need no regeneration of the world.
 */
const WANTED_BOARDS = BIOME_IDS.map((biome) => ({
    mob: WANTED_BOARD_MOB_ID,
    near: BIOMES[biome].village,
    dx: 3,
    dy: 3,
}));
/**
 * The world, and the regions' townsfolk of the quests stood by their places
 * (ADR 0047, `layouts/quest-keepers.ts`): after the retouches, before the
 * rooms, the same on the server, the client and the tests. The fallen of
 * the last stand are marked last (`withStoryFates`), keepers included.
 */
function withQuestKeepers(raw) {
    const zone = ZoneDefinitionSchema.parse(raw);
    const keepers = placeQuestKeepers([...QUEST_KEEPERS, ...WANTED_BOARDS], zone.landmarks, buildCollisionGrid(zone), zone.spawns);
    return withStoryFates(ZoneDefinitionSchema.parse({ ...zone, spawns: [...zone.spawns, ...keepers] }));
}
/**
 * The Company who fell in Shefira's Aire (ADR 0068 §4), wherever they stand
 * in the world: gone for a player once their last stand is made. Stamped as
 * the world loads, like the keepers, so the generator's output and the
 * admin's retouches need not know of it; the camp's Ser Givrecoeur, in the
 * tutorial's zone, is untouched.
 */
function withStoryFates(zone) {
    const fallen = new Set(COMPANY_FALLEN_MOBS);
    const marchers = new Set(COMPANY_MARCHERS);
    return {
        ...zone,
        spawns: zone.spawns.map((spawn) => {
            if (!fallen.has(spawn.mob))
                return spawn;
            // At the quarter they leave as the last stand is accepted; their
            // copies round the chasm (`appearsOn`) are where they went.
            const leaves = marchers.has(spawn.mob) && spawn.appearsOn === undefined;
            return {
                ...spawn,
                goneAfter: COMPANY_LAST_STAND,
                ...(leaves ? { leavesOn: COMPANY_LAST_STAND } : {}),
            };
        }),
    };
}
const DEFINITIONS = [
    ZoneDefinitionSchema.parse(buildTutorial(WORLD.spawn)),
    withQuestKeepers({
        id: 'world',
        name: 'Vallée de Shefira',
        width: WORLD_SIZE,
        height: WORLD_SIZE,
        spawn: WORLD.spawn,
        // The shrine stands a few steps north of the plaza, so a fallen player
        // is not dropped on top of a newcomer reading the welcome.
        respawn: WORLD.respawn,
        rows: WORLD.rows,
        ground: WORLD.ground,
        heights: WORLD.heights,
        props: WORLD.props,
        biomes: WORLD.biomes,
        landmarks: WORLD.landmarks,
        portals: WORLD.portals,
        spawns: WORLD.dens,
        // Into a cave, by its mouth or its summit's ladder: clicked, never walked into by chance.
        exits: WORLD.exits.map((exit) => ({
            ...exit,
            clickOnly: WORLD.caves.some((cave) => cave.id === exit.to),
        })),
        gates: AERIE_GATE === null ? [] : [{ zone: SHEFIRA_ZONE_ID, ...AERIE_GATE.area }],
        chests: WORLD.chests,
    }),
    // The cave under each summit (ADR 0021): in by the mouth, out on the top.
    ...WORLD.caves.map((cave) => ZoneDefinitionSchema.parse({
        id: cave.id,
        name: cave.name,
        width: cave.width,
        height: cave.height,
        spawn: cave.spawn,
        rows: cave.rows,
        ground: cave.ground,
        props: cave.props,
        biomes: cave.biomes,
        spawns: cave.dens,
        chests: cave.chests,
        exits: [
            {
                ...cave.down,
                width: 2,
                to: 'world',
                arrive: { x: cave.mouth.x, y: cave.mouth.y + 2 },
            },
            {
                ...cave.up,
                width: 2,
                to: 'world',
                arrive: { x: cave.ladder.x, y: cave.ladder.y + 2 },
                clickOnly: true,
            },
        ],
    })),
    // Each zone boss's dungeon (ADR 0027): in by its lair's facade, out by its door.
    ...WORLD.dungeons.map((dungeon) => ZoneDefinitionSchema.parse({
        id: dungeon.id,
        name: dungeon.name,
        width: DUNGEON_WIDTH,
        height: DUNGEON_HEIGHT,
        spawn: dungeon.spawn,
        rows: dungeon.rows,
        ground: dungeon.ground,
        props: dungeon.props,
        biomes: dungeon.biomes,
        spawns: dungeon.dens,
        landmarks: [
            { name: dungeon.name, ...dungeon.hall.centre, radius: dungeon.hall.radius, kind: 'lair' },
        ],
        // Its room never closes: the boss keeps its own clock, empty or not.
        persistent: true,
        exits: [
            {
                ...dungeon.door,
                width: 2,
                to: 'world',
                arrive: WORLD.dungeonArrivals.get(dungeon.id) ?? WORLD.spawn,
            },
        ],
    })),
    // Shefira's Aire (ADR 0068): an instance behind the crevasse of La Brèche.
    aerieZone(),
];
function aerieZone() {
    const aerie = buildShefiraAerie();
    return ZoneDefinitionSchema.parse({
        id: aerie.id,
        name: aerie.name,
        width: AERIE_WIDTH,
        height: AERIE_HEIGHT,
        spawn: aerie.spawn,
        respawn: aerie.respawn,
        rows: aerie.rows,
        ground: aerie.ground,
        props: aerie.props,
        biomes: aerie.biomes,
        spawns: aerie.dens,
        landmarks: [
            { name: aerie.name, ...aerie.arena.centre, radius: aerie.arena.radius, kind: 'lair' },
        ],
        instanced: true,
        exits: [
            {
                ...aerie.door,
                width: 2,
                to: 'world',
                arrive: AERIE_GATE?.arrival ?? WORLD.spawn,
            },
        ],
    });
}
export const ZONES = new Map(DEFINITIONS.map((zone) => [zone.id, zone]));
export const DEFAULT_ZONE_ID = 'world';
export function getZone(id) {
    return ZONES.get(id);
}
/** Build the collision grid of a zone. Callers cache it: rows never change at runtime. */
export function buildCollisionGrid(zone) {
    const grid = sizedGrid(zone, zone.rows, 'rows');
    checkGroundAgainstRows(zone);
    for (const placed of zone.props) {
        const box = footprintOf(placed);
        if (!grid.contains(box.x0, box.y0) || !grid.contains(box.x1, box.y1)) {
            throw new Error(`Zone "${zone.id}" places ${placed.prop} outside the map at (${String(placed.x)}, ${String(placed.y)})`);
        }
        if (PROPS[placed.prop]?.blocks !== true)
            continue;
        for (let y = box.y0; y <= box.y1; y += 1) {
            for (let x = box.x0; x <= box.x1; x += 1) {
                if (!inDoorway(placed, x, y))
                    grid.setBlocked(x, y, true);
            }
        }
    }
    // A chest stands on its tile like a blocking prop, open or shut.
    for (const chest of zone.chests) {
        const box = footprintOf({ prop: 'chest', x: chest.x, y: chest.y });
        for (let y = box.y0; y <= box.y1; y += 1) {
            for (let x = box.x0; x <= box.x1; x += 1)
                grid.setBlocked(x, y, true);
        }
    }
    if (!grid.isWalkable(zone.spawn.x, zone.spawn.y)) {
        throw new Error(`Zone "${zone.id}" spawns players inside a wall`);
    }
    for (const exit of zone.exits) {
        for (let y = exit.y; y < exit.y + exit.height; y += 1) {
            for (let x = exit.x; x < exit.x + exit.width; x += 1) {
                if (!grid.isWalkable(x, y)) {
                    throw new Error(`Zone "${zone.id}" has an exit into a wall at (${String(x)}, ${String(y)})`);
                }
            }
        }
    }
    const respawn = respawnPoint(zone);
    if (!grid.isWalkable(respawn.x, respawn.y)) {
        throw new Error(`Zone "${zone.id}" raises fallen players inside a wall`);
    }
    for (const portal of zone.portals) {
        if (!grid.isWalkable(portal.arrival.x, portal.arrival.y)) {
            throw new Error(`Zone "${zone.id}" lands travellers to ${portal.name} inside a wall`);
        }
    }
    for (const spawn of zone.spawns) {
        for (const at of [spawn, ...spawn.lairs]) {
            if (!grid.isWalkable(at.x, at.y)) {
                throw new Error(`Zone "${zone.id}" spawns ${spawn.mob} inside a wall at (${String(at.x)}, ${String(at.y)})`);
            }
        }
    }
    return grid;
}
function sizedGrid(zone, rows, layer) {
    const grid = CollisionGrid.fromRows(rows);
    if (grid.width !== zone.width || grid.height !== zone.height) {
        throw new Error(`Zone "${zone.id}" declares ${String(zone.width)}x${String(zone.height)} but its ${layer} are ${String(grid.width)}x${String(grid.height)}`);
    }
    return grid;
}
/**
 * The ground as the tiles see it (`TerrainField`), from the zone's `ground`
 * rows, or from its collision when it has none: blocked is dirt, the rest
 * grass. Not collision: the client paints with it and nobody paths on it.
 */
export function terrainFieldOf(zone) {
    const ground = zone.ground ?? zone.rows.map((row) => row.replace(/./g, (c) => (c === '#' ? 'd' : '.')));
    if (ground.length !== zone.height || ground.some((row) => row.length !== zone.width)) {
        throw new Error(`Zone "${zone.id}" has ground rows that do not match its size`);
    }
    const heights = zone.heights ?? [];
    if (heights.length > 0 &&
        (heights.length !== zone.height || heights.some((row) => row.length !== zone.width))) {
        throw new Error(`Zone "${zone.id}" has height rows that do not match its size`);
    }
    return new TerrainField(ground, heights);
}
/**
 * Collision and ground must agree: a tile whose terrain cannot be walked is
 * a wall, and a wall that is not a prop is an unwalkable terrain or a north
 * bank, painted half wet (ADR 0081).
 */
function checkGroundAgainstRows(zone) {
    if (zone.ground === undefined)
        return;
    zone.ground.forEach((row, y) => {
        for (let x = 0; x < row.length; x += 1) {
            const char = row[x] ?? '.';
            if (!isTerrainChar(char)) {
                throw new Error(`Zone "${zone.id}" paints an unknown terrain "${char}" at (${String(x)}, ${String(y)})`);
            }
            const wall = zone.rows[y]?.[x] === '#';
            const walkable = terrainOf(char).walkable;
            const bank = wall && walkable && isNorthBank(char, zone.ground?.[y + 1]?.[x]);
            if (walkable === wall && !bank) {
                throw new Error(`Zone "${zone.id}" disagrees with itself at (${String(x)}, ${String(y)}): ${terrainOf(char).id} but ${wall ? 'a wall' : 'walkable'}`);
            }
        }
    });
}
/** The biome of a tile, when the zone has biomes. */
export function biomeAt(zone, tile) {
    const digit = zone.biomes?.[tile.y]?.[tile.x];
    return digit === undefined ? undefined : BIOME_IDS[Number(digit)];
}
/** Where a fallen player gets back up in this zone. */
export function respawnPoint(zone) {
    return zone.respawn ?? zone.spawn;
}
/** The gate into an instanced zone a tile belongs to, if any (ADR 0068). */
export function gateAt(zone, tile) {
    return zone.gates.find((gate) => tile.x >= gate.x &&
        tile.x < gate.x + gate.width &&
        tile.y >= gate.y &&
        tile.y < gate.y + gate.height);
}
/** Rings from a gate's rectangle to a tile: 0 on it, 1 on its rim. */
export function gateDistance(gate, tile) {
    const dx = Math.max(gate.x - tile.x, 0, tile.x - (gate.x + gate.width - 1));
    const dy = Math.max(gate.y - tile.y, 0, tile.y - (gate.y + gate.height - 1));
    return Math.max(dx, dy);
}
/**
 * Where a click on a gate walks to: the open tile of its rim (one ring out,
 * or inside its rectangle when the gate is walkable there) nearest the
 * clicked tile. The crevasse of La Brèche is a hole: one walks to its edge
 * and asks there. Undefined when no tile of the rim is open.
 */
export function gateApproach(gate, click, isOpen) {
    let best;
    let bestDistance = Infinity;
    for (let y = gate.y - 1; y <= gate.y + gate.height; y += 1) {
        for (let x = gate.x - 1; x <= gate.x + gate.width; x += 1) {
            if (!isOpen(x, y))
                continue;
            const distance = Math.hypot(x - click.x, y - click.y);
            if (distance < bestDistance) {
                best = { x, y };
                bestDistance = distance;
            }
        }
    }
    return best;
}
/** The zone's gate into `zoneId`, if it has one. */
export function gateInto(zone, zoneId) {
    return zone.gates.find((gate) => gate.zone === zoneId);
}
/** The exit a tile belongs to, if any. */
export function exitAt(zone, tile) {
    return zone.exits.find((exit) => tile.x >= exit.x &&
        tile.x < exit.x + exit.width &&
        tile.y >= exit.y &&
        tile.y < exit.y + exit.height);
}
/**
 * The exit a click on a building leads to: a prop whose silhouette covers
 * the tile (footprint and the rows rising above it, like the auction hall's
 * facade, or spilling round it, like a cave's arch of daylight) and whose footprint holds an exit or has one right before it (a
 * fortress's doorway, a cave's mouth, a summit's hatch). The walk goes to the
 * tile of that exit nearest the click.
 */
export function exitBuildingAt(zone, tile) {
    for (const prop of zone.props) {
        const box = footprintOf(prop);
        const drawn = silhouetteOf(prop);
        if (tile.x < drawn.x0 || tile.x > drawn.x1 || tile.y < drawn.y0 || tile.y > drawn.y1) {
            continue;
        }
        let best;
        let bestDistance = Infinity;
        // A gate into an instance (ADR 0068) is walked to like an exit, and asked at.
        for (const exit of [...zone.exits, ...zone.gates]) {
            for (let y = exit.y; y < exit.y + exit.height; y += 1) {
                for (let x = exit.x; x < exit.x + exit.width; x += 1) {
                    if (x < box.x0 || x > box.x1 || y < box.y0 || y > box.y1 + 1)
                        continue;
                    const distance = Math.abs(x - tile.x) + Math.abs(y - tile.y);
                    if (distance < bestDistance) {
                        best = { x, y };
                        bestDistance = distance;
                    }
                }
            }
        }
        if (best !== undefined)
            return best;
    }
    return undefined;
}
//# sourceMappingURL=zones.js.map